import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import {
  Rarity as RarityConst,
  RevealableProperty,
} from '@sorare/core/src/__generated__/globalTypes';
import { SnowBackground } from '@sorare/core/src/atoms/animations/SnowBackground';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BodyL,
  BodyS,
  BrandM,
  HeadlineXS,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { formatWord } from '@sorare/core/src/lib/humanize';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { getClueAssets } from '#src/lib/crafting.ts';

import type { Clues_cardPullConfig } from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical)`
  padding: var(--double-unit);
`;

const List = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const CluesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: var(--unit);
  @media ${laptopAndAbove} {
    flex-direction: row;
    flex-wrap: wrap;
    > * {
      min-width: var(--clue-max-width, 200px);
    }
  }
`;

const SnowWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const Clue = styled(Vertical)`
  flex: 1;
  border: 1px solid var(--c-nd-50);
  border-radius: var(--unit);
  padding: var(--double-unit);
  position: relative;
  & > svg {
    height: var(--quadruple-unit) !important;
    width: var(--quadruple-unit) !important;
  }
`;

const CluesSubtitle = styled(LabelL)`
  @media ${laptopAndAbove} {
    max-width: 70%;
  }
`;

const Icon = styled.img`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
`;

type Props = {
  cardPullConfigs: Clues_cardPullConfig[];
  rarity: Rarity;
};

export const Clues = ({ cardPullConfigs, rarity }: Props) => {
  const { sport } = useSportContext();
  const cluesAssets = getClueAssets(sport);
  const clues =
    cardPullConfigs.at(0)?.revealCosts.map(cost => cost.propertyName) ?? [];

  return (
    <Wrapper center gap={3}>
      <Vertical center gap={3}>
        <Vertical center>
          <BrandM className="text-center">
            <FormattedMessage defaultMessage="Want a better shot? Use Drawing Clues" />
          </BrandM>
          <CluesSubtitle className="text-center" color="var(--c-nd-600)">
            <FormattedMessage defaultMessage="Need a hint? Drawing Clues reveal card details" />
          </CluesSubtitle>
        </Vertical>

        <CluesWrapper
          style={
            {
              '--clue-max-width': clues.length % 3 > 1 ? '250px' : '200px',
            } as React.CSSProperties
          }
        >
          {clues.map(key => (
            <Clue key={cluesAssets[key].label.defaultMessage} center>
              {key === RevealableProperty.RESHUFFLE && (
                <SnowWrapper>
                  <SnowBackground />
                </SnowWrapper>
              )}
              <Icon src={cluesAssets[key].iconUrl} alt="" />
              <BodyL bold className="text-center">
                <FormattedMessage {...cluesAssets[key].label} />
              </BodyL>
              <LabelL
                color="var(--c-nd-600)"
                className="text-center text-pretty"
                style={{ flex: 1 }}
              >
                <FormattedMessage {...cluesAssets[key].description} />
              </LabelL>
              <BodyS bold>
                <Horizontal gap={0.5}>
                  <FormattedMessage
                    defaultMessage="Cost {nb} to use"
                    values={{
                      nb: (
                        <>
                          {
                            cardPullConfigs[0].revealCosts.find(
                              c => c.propertyName === key
                            )?.amountInCurrency
                          }
                          <img
                            style={{
                              width: 'var(--double-unit)',
                              height: 'var(--double-unit)',
                            }}
                            src={cluesAssets[key].iconUrl}
                            alt=""
                          />
                        </>
                      ),
                    }}
                  />
                </Horizontal>
              </BodyS>
              <List>
                {cardPullConfigs.map(config => (
                  <li key={config.name}>
                    <LabelS color="var(--c-nd-600)">
                      <FormattedMessage
                        defaultMessage="or {nb} for a {name} draw"
                        values={{
                          nb: (
                            <>
                              {
                                config.revealCosts.find(
                                  c => c.propertyName === key
                                )?.amountInShards
                              }
                              <Shards
                                color={getRarityColor(rarity)}
                                size={1.5}
                              />
                            </>
                          ),
                          name: formatWord(config.name),
                        }}
                      />
                    </LabelS>
                  </li>
                ))}
              </List>
            </Clue>
          ))}
        </CluesWrapper>
      </Vertical>
      <LabelL color="var(--c-nd-600)">
        <FormattedMessage defaultMessage="Cost of using a clue:" />
      </LabelL>
      <Horizontal style={{ width: '100%' }}>
        {[
          RarityConst.limited,
          RarityConst.rare,
          RarityConst.super_rare,
          RarityConst.unique,
        ].map((r, index) => (
          <Bezel
            key={r}
            color={getRarityColor(r)}
            style={{
              width: '100%',
              padding: 'var(--unit)',
              color: getRarityColor(r),
            }}
            {...{
              bezelSize: 5,
              radius: 0,
              borderWidth: 1,
              borderColor: `linear-gradient(to bottom, var(--c-nd-100) 80%, currentColor 100%)`,
              boxShadow: `inset 0px -20px 20px -20px currentColor`,
              background:
                'conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 25%, var(--c-black) 0) 0 0/5px 5px',
            }}
          >
            <SBHorizontal>
              <HeadlineXS color={getRarityColor(r)}>{formatWord(r)}</HeadlineXS>
              <LabelM color="var(--c-white)">
                <FormattedMessage
                  defaultMessage="Cost x{multiplier}"
                  values={{ multiplier: Math.pow(2, index) }}
                />
              </LabelM>
            </SBHorizontal>
          </Bezel>
        ))}
      </Horizontal>
    </Wrapper>
  );
};

Clues.fragments = {
  cardPullConfig: gql`
    fragment Clues_cardPullConfig on CardPullConfig {
      name
      revealCosts {
        propertyName
        amountInShards
        amountInCurrency
      }
    }
  ` as TypedDocumentNode<Clues_cardPullConfig>,
};
